import { useCallback, useRef } from "react";
import type { GridReadyEvent, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { RollMaterial } from "../models/RollMaterial";
import { useTableData } from "../hooks/useTableData";
import { useArrowRowNavigation } from "@/controls/ag-grid/useArrowRowNavigation";
import {
	LIST_HEADER_HEIGHT,
	LIST_ROW_HEIGHT,
	listColDef,
} from "@/controls/ag-grid/listGrid";
import {
	OneLineCellStyles,
	LIST_ROW_CLASS,
} from "@/controls/ag-grid/OneLineCells";

interface RollMaterialsTableProps {
	rollMaterials: RollMaterial[];
	selectedMaterial: RollMaterial | null;
	onRowSelected: (material: RollMaterial | null) => void;
	onDelete: (material: RollMaterial) => void;
}

export const RollMaterialsTable = ({
	rollMaterials,
	selectedMaterial,
	onRowSelected,
	onDelete,
}: RollMaterialsTableProps) => {
	const gridRef = useRef<AgGridReact<RollMaterial>>(null);
	const { columnDefs } = useTableData({ onDelete });

	const onGridReady = useCallback((_params: GridReadyEvent<RollMaterial>) => {
		// Let flex columns handle sizing naturally
	}, []);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<RollMaterial>) => {
			if (event.data) {
				onRowSelected(event.data);
			}
		},
		[onRowSelected],
	);

	const onCellKeyDown = useArrowRowNavigation<RollMaterial>(onRowSelected);

	const getRowId = useCallback((params: { data: RollMaterial }) => {
		return params.data.Id?.toString() ?? "";
	}, []);

	return (
		<>
			<OneLineCellStyles />
			<AgGridReact<RollMaterial>
				ref={gridRef}
				rowData={rollMaterials}
				columnDefs={columnDefs}
				defaultColDef={listColDef(globalDefaultColDef)}
				onGridReady={onGridReady}
				onRowClicked={onRowClicked}
				onCellKeyDown={onCellKeyDown}
				getRowId={getRowId}
				rowSelection="single"
				suppressRowClickSelection={false}
				suppressContextMenu
				alwaysShowVerticalScroll
				suppressColumnVirtualisation
				animateRows={false}
				enableBrowserTooltips
				rowClass={LIST_ROW_CLASS}
				rowHeight={LIST_ROW_HEIGHT}
				headerHeight={LIST_HEADER_HEIGHT}
				getRowStyle={(params) => {
					if (selectedMaterial?.Id === params.data?.Id) {
						return { backgroundColor: "#e3f2fd", color: "inherit" };
					}
					if (params.data && !params.data.Active) {
						return { backgroundColor: "#f5f5f5", color: "#9e9e9e" };
					}
					return undefined;
				}}
			/>
		</>
	);
};
